import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Badge,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Button } from "../components/Buttons";
import {
  adminListOfflineCollections,
  adminApproveOfflineCollection,
  adminRejectOfflineCollection,
} from "../api";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

export default function AdminOfflineCollections() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const [method, setMethod] = useState("all");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  /* Review dialog */
  const [review, setReview] = useState(null);
  const [approveAmount, setApproveAmount] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("approve"); // "approve" | "reject"

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status !== "all") params.status = status;
    if (method !== "all") params.method = method;
    if (appliedSearch) params.search = appliedSearch;
    adminListOfflineCollections(params)
      .then(({ data }) => {
        setRows(data.collections || []);
        setTotal(data.pagination?.total || 0);
        setPendingCount(data.pendingCount || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, status, method, appliedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = (row, nextMode) => {
    setReview(row);
    setMode(nextMode);
    setApproveAmount(String(row.amount));
    setRejectReason("");
    setFeedback(null);
  };

  const handleApprove = async () => {
    setBusy(true);
    try {
      const { data } = await adminApproveOfflineCollection(review.id, {
        amount: Number(approveAmount),
      });
      setReview(null);
      setFeedback({
        severity: "success",
        msg: `Approved — ${INR(data.netAmount ?? approveAmount)} credited to the user, ${INR(
          data.totalCommission || 0,
        )} commission shared upline.`,
      });
      load();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not approve",
      });
      setReview(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      await adminRejectOfflineCollection(review.id, rejectReason.trim());
      setReview(null);
      setFeedback({ severity: "success", msg: "Request rejected." });
      load();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not reject",
      });
    } finally {
      setBusy(false);
    }
  };

  const amountChanged =
    review && Number(approveAmount) !== Number(review.amount);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="h5" fontWeight={700}>
              POS & QR Approvals
            </Typography>
            {pendingCount > 0 && (
              <Badge
                badgeContent={pendingCount}
                color="warning"
                sx={{ "& .MuiBadge-badge": { fontWeight: 700 } }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Verify each UTR against the merchant statement before approving —
            approval credits the wallet and pays commission up the chain.
          </Typography>
        </Box>

        <Stack direction="row" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search UTR, name, mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                setAppliedSearch(search.trim());
              }
            }}
            onBlur={() => {
              if (search.trim() !== appliedSearch) {
                setPage(1);
                setAppliedSearch(search.trim());
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                      setAppliedSearch("");
                    }}
                  >
                    <ClearRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 240 }}
          />
          <TextField
            select
            size="small"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="all">All status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">POS & QR</MenuItem>
            <MenuItem value="pos">POS only</MenuItem>
            <MenuItem value="qr">QR only</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : rows.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <ReceiptLongRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No requests match these filters
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1020 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Submitted</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Paid to</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>UTR</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(r.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {r.userName || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.userMobile}
                        {r.roleName ? ` · ${r.roleName.replace(/_/g, " ")}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={
                          r.method === "pos" ? (
                            <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <QrCode2RoundedIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        label={r.method === "pos" ? "POS" : "QR"}
                        sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {r.qrLabel || r.machineLabel || "—"}
                      </Typography>
                      {r.machineTerminalId && (
                        <Typography variant="caption" color="text.secondary">
                          TID {r.machineTerminalId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {INR(r.approvedAmount ?? r.amount)}
                      {r.approvedAmount !== null &&
                        Number(r.approvedAmount) !== Number(r.amount) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            submitted {INR(r.amount)}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {r.utr}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.rejectionReason || ""}>
                        <Chip
                          size="small"
                          color={STATUS_COLOR[r.status]}
                          label={r.status}
                          sx={{
                            height: 24,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" gap={0.5} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openReview(r, "approve")}
                          sx={{
                            height: 34,
                            px: 2,
                            fontSize: 13,
                            borderRadius: 2,
                            textTransform: "none",
                          }}
                        >
                          Review
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      {total > 20 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={Math.ceil(total / 20)}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Stack>
      )}

      {/* ---------------- Review dialog ---------------- */}
      <Dialog
        open={!!review}
        onClose={() => !busy && setReview(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle fontWeight={700}>
          Review {review?.method === "pos" ? "POS" : "QR"} collection
        </DialogTitle>
        <DialogContent dividers>
          {review && (
            <Grid container spacing={3}>
              {/* Proof */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Payment proof
                </Typography>
                {review.screenshotUrl && review.screenshotIsPdf ? (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<OpenInNewRoundedIcon />}
                    onClick={() =>
                      window.open(review.screenshotUrl, "_blank", "noopener")
                    }
                    sx={{
                      height: "auto",
                      py: 3,
                      borderRadius: 2,
                      borderStyle: "dashed",
                    }}
                  >
                    Open PDF slip
                  </Button>
                ) : review.screenshotUrl ? (
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "action.hover",
                    }}
                  >
                    <Box
                      component="img"
                      src={review.screenshotUrl}
                      alt="Payment proof"
                      sx={{
                        width: "100%",
                        maxHeight: 420,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                    <Tooltip title="Open full size">
                      <IconButton
                        size="small"
                        onClick={() =>
                          window.open(review.screenshotUrl, "_blank", "noopener")
                        }
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                      >
                        <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    No screenshot was attached to this request.
                  </Alert>
                )}
              </Grid>

              {/* Details + action */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Details
                </Typography>
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                  {[
                    ["User", `${review.userName || "—"} · ${review.userMobile || ""}`],
                    ["Role", (review.roleName || "—").replace(/_/g, " ")],
                    [
                      "Paid to",
                      review.qrLabel ||
                        `${review.machineLabel || "—"}${
                          review.machineTerminalId
                            ? ` · TID ${review.machineTerminalId}`
                            : ""
                        }`,
                    ],
                    ["UTR", review.utr],
                    ["Submitted", formatDate(review.createdAt)],
                    ["Amount entered", INR(review.amount)],
                  ].map(([label, value]) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          textAlign: "right",
                          fontFamily: label === "UTR" ? "monospace" : undefined,
                        }}
                      >
                        {value}
                      </Typography>
                    </Stack>
                  ))}
                  {review.remarks && (
                    <Box
                      sx={(theme) => ({
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        borderRadius: 1.5,
                        p: 1.25,
                      })}
                    >
                      <Typography variant="caption" color="text.secondary">
                        User's remarks
                      </Typography>
                      <Typography variant="body2">{review.remarks}</Typography>
                    </Box>
                  )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                {review.status !== "pending" ? (
                  <Alert
                    severity={review.status === "approved" ? "success" : "error"}
                  >
                    Already {review.status}
                    {review.reviewedAt ? ` on ${formatDate(review.reviewedAt)}` : ""}
                    {review.rejectionReason ? ` — ${review.rejectionReason}` : ""}
                  </Alert>
                ) : mode === "approve" ? (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      label="Amount to credit"
                      type="number"
                      value={approveAmount}
                      onChange={(e) => setApproveAmount(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      helperText="Correct this if the slip shows a different amount"
                    />
                    {amountChanged && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Crediting {INR(approveAmount)} instead of the{" "}
                        {INR(review.amount)} the user entered. Commission is
                        calculated on the corrected amount.
                      </Alert>
                    )}
                  </>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="Reason for rejection"
                    multiline
                    minRows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. UTR not found in the merchant statement"
                  />
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReview(null)} disabled={busy}>
            Close
          </Button>
          {review?.status === "pending" &&
            (mode === "approve" ? (
              <>
                <Button
                  color="error"
                  startIcon={<CancelRoundedIcon />}
                  onClick={() => setMode("reject")}
                  disabled={busy}
                >
                  Reject instead
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleRoundedIcon />}
                  loading={busy}
                  disabled={busy || !(Number(approveAmount) > 0)}
                  onClick={handleApprove}
                >
                  Approve &amp; credit {INR(approveAmount)}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setMode("approve")} disabled={busy}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  loading={busy}
                  disabled={busy || !rejectReason.trim()}
                  onClick={handleReject}
                >
                  Reject request
                </Button>
              </>
            ))}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
